import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import * as Atom from '../recoil';
import {
  beaconSelector,
  isActiveStep5,
  saveConditions,
} from '../recoil/selector';
import useNav from '../hooks/useNav';
import { useMtgConditions } from './useMtgConditions';
import { useBaseParam } from '../hooks/useBaseParam';
import useToast from '../hooks/useToast';
import useBeacon from '../hooks/useBeacon';
import { putLoanProcessPreRequest } from '../../infra/api/putLoanProcessPreRequest';
import { validateCarNo } from '../helper/validator';
import { PAGE_NM } from '../types/types';

const useStep5 = () => {
  const beaconData = useRecoilValue(beaconSelector);
  const beacon = useBeacon({
    mediaCd: beaconData.mediaCd,
    custSeq: beaconData.custSeq,
    srcCd: beaconData.srcCd,
    pageName: PAGE_NM.CONDITIONS_STEP5,
  });
  useEffect(() => {
    beacon.sendViewPage();
  }, []);

  const { navigate } = useNav();
  const { media } = useParams();
  const { addToast } = useToast();
  const [personal, setPersonal] = useRecoilState(Atom.personalInfo);
  const isActive = useRecoilValue(isActiveStep5);
  const save = useRecoilValue(saveConditions);
  const [ctgGrpList] = useRecoilState(Atom.commonCodes);
  const { mortgageInfo } = useMtgConditions();
  const houseTyp = ctgGrpList[5].list;
  const healthPayer = ctgGrpList[3].list;
  const debtTyp = ctgGrpList.find(
    ({ codeGrp }: any) => codeGrp === 'C056',
  )?.list;
  const carOwnerYn = [
    { codeNm: '없음', code: 'N' },
    { codeNm: '차량 보유', code: 'Y' },
  ];
  const carNo = '';
  const debtYn = [
    { codeNm: '아니오', code: 'N' },
    { codeNm: '예', code: 'Y' },
  ];
  const [warningText, setWarningText] = useState<string>('');
  const location = useLocation();
  const props: any = location?.state;
  const [isReuse, setIsReuse] = useState<boolean>(true);

  const additionalMortgageInfo: any = {};

  if (
    mortgageInfo?.isApartment?.value === 'Y' &&
    mortgageInfo?.ldongCode?.value
  ) {
    if (mortgageInfo?.rateSeq?.value) {
      additionalMortgageInfo.rateSeq = mortgageInfo?.rateSeq?.value;
      additionalMortgageInfo.ldongCode = mortgageInfo?.ldongCode?.value;
    } else if (mortgageInfo?.hopefulAmt) {
      additionalMortgageInfo.houseAmt = mortgageInfo?.hopefulAmt * 10000;
      additionalMortgageInfo.ldongCode = mortgageInfo?.ldongCode?.value;
    }
  }

  const putPreRequest = useBaseParam(putLoanProcessPreRequest, {
    ...save,
    ...additionalMortgageInfo,
    agreeYn: props?.agreeYn ?? 'N', // 프로모션
    monthlyRequestYn: props?.monthlyRequestYn ?? 'N', // 자동 가심사
    reuseYn: isReuse ? 'Y' : 'N', // 입력 정보 재사용
    scrapingYn: save?.scrapingYn || 'N', // 스크래핑 사용 여부
  });

  const submitPersonalInfo = async () => {
    beacon.sendClkBtn({
      events: { subEventName: 'step5' },
    });
    const res: any = await putPreRequest();
    if (res?.data?.isSucceed === true) {
      navigate('/loading');
    }
  };

  const setReuseYn = () => {
    setIsReuse((v) => !v);
  };

  const setHouseTyp = (v: string) => {
    setPersonal((prev: any) => {
      return {
        ...prev,
        houseTyp: v,
      };
    });
  };

  const setHealthPayer = (v: string) => {
    setPersonal((prev: any) => {
      return {
        ...prev,
        healthPayerTyp: v,
      };
    });
  };

  const onClickHealthPayer = (v: any) => {
    setHealthPayer(v);
  };

  const setCarOwnerYn = (v: string) => {
    setPersonal((prev: any) => {
      return {
        ...prev,
        carOwnerYn: v,
      };
    });
  };

  const setCarNo = (v: string) => {
    setPersonal((prev: any) => {
      return {
        ...prev,
        carNo: v,
      };
    });
  };
  const onClickHouseTyp = (v: any) => {
    setHouseTyp(v);
  };
  const onClickCarOwnerYn = (v: any) => {
    setCarOwnerYn(v);
  };
  const onClickDebtYn = (v: any) => {
    setPersonal((prev: any) => {
      return {
        ...prev,
        debtYn: v,
      };
    });
  };
  const onClickDebtTyp = (v: any) => {
    setPersonal((prev: any) => {
      return {
        ...prev,
        debtTyp: v,
      };
    });
  };
  const onChangeCarNo = (value: string) => {
    if (!validateCarNo(value) && value.length > 0 && value.length < 9) {
      setWarningText('차량번호를 다시 확인 해주세요');
    } else {
      setWarningText('');
    }
    if (value.length < 9) {
      setCarNo(value);
    } else {
      return;
    }
  };

  useEffect(() => {
    if (personal?.carOwnerYn !== 'Y') {
      setCarNo('');
    }
    setWarningText('');
  }, [personal?.carOwnerYn]);

  useEffect(() => {
    if (personal?.debtYn !== 'Y') {
      setPersonal((prev: any) => ({
        ...prev,
        debtTyp: '',
      }));
    }
  }, [personal?.debtYn]);

  return {
    submitPersonalInfo,
    houseTyp,
    healthPayer,
    carOwnerYn,
    carNo,
    debtYn,
    debtTyp,
    personal,
    isActive,
    onClickHouseTyp,
    onClickCarOwnerYn,
    onClickDebtYn,
    onClickDebtTyp,
    onClickHealthPayer,
    onChangeCarNo,
    warningText,
    isReuse,
    setReuseYn,
    isKT: media === 'kt',
  };
};

export default useStep5;
